import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Autocomplete,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material";
import api from "../../../../services/api";

const customFilter = createFilterOptions({
  stringify: (option) =>
    `${option.name} ${option.flavor || ""} ${option.groupLabel || ""}`,
});

const ProductDiscountFields = ({ form, handleNestedChange, setForm }) => {
  const [productOptions, setProductOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const selectedProductId =
    form.appliesTo?.productTargets?.[0]?.productId || "";

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get("/admin/products/all");
        const withGroupLabels = res.data.map((item) => {
          const group =
            item.type === "patisserie" && item.subcategory
              ? `Patisserie → ${item.subcategory}`
              : productTypeLabels[item.type] || item.type;

          return { ...item, groupLabel: group.toLowerCase() };
        });
        setProductOptions(withGroupLabels);
      } catch (err) {
        console.error("Failed to fetch products for discount selector:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleProductSelect = (product) => {
    setForm((prev) => ({
      ...prev,
      appliesTo: {
        ...prev.appliesTo,
        productTargets: [
          {
            productId: product._id,
            productType: product.subcategory || product.type,
          },
        ],
      },
    }));
  };

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={12}>
        <Autocomplete
          fullWidth
          options={productOptions}
          filterOptions={customFilter}
          groupBy={(option) =>
            option.type === "patisserie"
              ? `Patisserie → ${option.subcategory}`
              : productTypeLabels[option.type] || option.type
          }
          getOptionLabel={(option) =>
            option.flavor ? `${option.name} (${option.flavor})` : option.name
          }
          loading={loading}
          value={
            productOptions.find((p) => p._id === selectedProductId) || null
          }
          onChange={(e, newVal) => newVal && handleProductSelect(newVal)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Product"
              placeholder="Start typing..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderGroup={(params) => (
            <li key={params.key}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ px: 2, pt: 1, pb: 0.5, color: "text.primary" }}>
                {params.group}
              </Typography>
              <ul style={{ paddingLeft: 16 }}>{params.children}</ul>
            </li>
          )}
          renderOption={(props, option) => (
            <li {...props} key={option._id}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {option.flavor
                  ? `${option.name} (${option.flavor})`
                  : option.name}
              </Typography>
            </li>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Discount Value"
          type="number"
          value={form.config.discountValue || ""}
          onChange={(e) =>
            handleNestedChange(
              "config",
              "discountValue",
              Number(e.target.value)
            )
          }
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          fullWidth
          select
          label="Discount Type"
          value={form.config.discountType || "percentage"}
          onChange={(e) =>
            handleNestedChange("config", "discountType", e.target.value)
          }>
          <MenuItem value="percentage">%</MenuItem>
          <MenuItem value="fixed">$</MenuItem>
        </TextField>
      </Grid>
    </Grid>
  );
};

const productTypeLabels = {
  standardCake: "Standard Cakes",
  specialtyCake: "Specialty Cakes",
  cheesecake: "Cheesecakes",
  patisserie: "Patisserie",
  grazeBox: "Graze Boxes",
  accessory: "Accessories",
};

export default ProductDiscountFields;
